import React, { useEffect, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Button, Skeleton } from 'antd'
import { ExclamationOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { ReactComponent as Logo } from '../../assets/logo.svg'
import { getAppWallets, verifyAppToken } from '../../services/apiService'
import {
  isExpired,
  isOk,
  useWogPay,
} from '../main/components/RefuelModal/useWogPay'
import {
  isSharedWallet,
  walletsAdapter,
} from '../main/components/RefuelModal/adapters'
import {
  readMessage,
  readStatus,
} from '../main/components/RefuelModal/constants'
import { IRefuelWallet } from '../main/components/RefuelModal/types'
import RefuelHeader from '../main/components/RefuelModal/RefuelHeader'
import RefuelBody from '../main/components/RefuelModal/RefuelBody'
import RefuelFooter from '../main/components/RefuelModal/RefuelFooter'
import Guide from './Guide'
import { readLink } from './link'

/*
    WOG Pay for app users. The mobile app opens this page with the phone and a
    partner token in the link, so there is no cabinet login — every call is
    signed with what the link carries. The flow is the cabinet's refuel modal
    with one choice in front of it: the wallet, which the cabinet picks in its
    header instead — and one choice fewer: the account is the one the token is
    bound to, so there is no account picker. The stations are fetched as soon
    as the token is confirmed, before any wallet is chosen; the leftovers under
    the wallet picker come from Mobile/Balances of the wallet chosen. Nothing
    is asked with the token until AuthorizationAPP has confirmed it.
 */

const FIRST_STEP = 'Оберіть рахунок та АЗК'

/**
 * The wallet the driver almost certainly wants. Mobile/Wallets adds the shared
 * «Власний» wallet to everyone's list, so a driver with one wallet of their
 * own gets that one picked and goes on; «Власний» stays in the list to be
 * chosen by hand. The choice is left open only when there are several of the
 * driver's own — or picked anyway when «Власний» is all there is.
 */
const preselect = (wallets: IRefuelWallet[]) => {
  const own = wallets.filter((wallet) => !isSharedWallet(wallet.id))
  if (own.length === 1) return own[0].id
  return wallets.length === 1 ? wallets[0].id : undefined
}

interface IFProblem {
  title: string
  text: string
}

/** Why the link cannot be used, shown instead of the flow. */
interface IFRefusal extends IFProblem {
  /** The check itself failed to run, so it can be tried again. */
  retry?: boolean
}

const EXPIRED: IFRefusal = {
  title: 'Посилання недійсне або застаріло',
  text: 'Відкрийте WOG Pay у застосунку WOG ще раз — застосунок створить нове посилання.',
}

/**
 * What a refused token check means to the driver. A wrong token, or a valid
 * answer that still says no, needs a new link from the app; a wrong or
 * blocked phone gets the backend's own explanation.
 */
const refusal = (res: any): IFRefusal => {
  const status = readStatus(res)
  if (status === 5 || status === 0) return EXPIRED
  return {
    title: 'Не вдалося підтвердити посилання',
    text: readMessage(res) || EXPIRED.text,
  }
}

const Problem: React.FC<IFProblem> = ({ title, text, children }) => (
  <div className="refuel-status">
    <div className="refuel-status__icon warn">
      <ExclamationOutlined />
    </div>
    <div className="refuel-status__title">{title}</div>
    <div className="refuel-status__text">{text}</div>
    {children}
  </div>
)

const WogPay: React.FC<RouteComponentProps> = ({ location }) => {
  const link = useMemo(() => readLink(location.search), [location.search])
  const { session } = link

  const [loadingWallets, setLoadingWallets] = useState<boolean>(false)
  const [walletsError, setWalletsError] = useState<string>()
  const [wallets, setWallets] = useState<IRefuelWallet[]>([])
  const [wallet, setWallet] = useState<string>()
  /** The token check is in flight. */
  const [checking, setChecking] = useState<boolean>(false)
  /** The token check passed — only then is anything else asked with the token. */
  const [verified, setVerified] = useState<boolean>(false)
  /** The token was refused, by the check or by a later call. */
  const [refused, setRefused] = useState<IFRefusal>()
  /** «Як це працює», behind the ? in the header. */
  const [guide, setGuide] = useState<boolean>(false)

  /*
      A lapsed token is re-recorded by AuthorizationAPP behind the scenes (see
      withAppToken); only when that is refused does the flow stop, with the
      backend's own reason when it gave one.
   */
  const expire = (res?: any) =>
    setRefused(res && res.tokenRefused ? refusal(res) : EXPIRED)

  const loadWallets = () => {
    if (!session) return
    setLoadingWallets(true)
    setWalletsError(undefined)
    getAppWallets(session).then((res) => {
      setLoadingWallets(false)
      if (isExpired(res)) return expire()
      if (!isOk(res)) {
        setWalletsError(readMessage(res) || 'Не вдалося завантажити рахунки')
        return
      }
      const list = walletsAdapter(res.Result)
      setWallets(list)
      setWallet(preselect(list))
    })
  }

  /* The token is confirmed before anything else is asked with it. */
  const verifyLink = () => {
    if (!session) return
    setChecking(true)
    setRefused(undefined)
    setVerified(false)
    verifyAppToken(session).then((res) => {
      setChecking(false)
      if (res.error === true) {
        setRefused({
          title: 'Не вдалося підтвердити посилання',
          text: 'Перевірте з’єднання і спробуйте ще раз.',
          retry: true,
        })
        return
      }
      if (!isOk(res) || res.Result?.ValidToken !== true) {
        setRefused(refusal(res))
        return
      }
      setVerified(true)
      loadWallets()
    })
  }

  useEffect(verifyLink, [session]) // eslint-disable-line react-hooks/exhaustive-deps

  const pay = useWogPay({
    visible: !!session && verified && !refused,
    wallet,
    session,
    coords: link.coords,
    onExpired: expire,
  })

  /** The flow is on screen, with its header and footer around it. */
  const flow =
    !!session &&
    !refused &&
    !checking &&
    !loadingWallets &&
    !walletsError &&
    !!wallets.length

  const body = () => {
    if (!session) {
      return (
        <Problem
          title="Посилання неповне"
          text="У ньому немає токена або номера телефону. Відкрийте WOG Pay у застосунку WOG ще раз."
        />
      )
    }

    if (refused) {
      return (
        <Problem title={refused.title} text={refused.text}>
          {refused.retry && (
            <Button type="primary" onClick={verifyLink}>
              Спробувати ще раз
            </Button>
          )}
        </Problem>
      )
    }

    if (checking || loadingWallets) {
      return <Skeleton active paragraph={{ rows: 6 }} />
    }

    if (walletsError) {
      return (
        <Problem title="Не вдалося завантажити рахунки" text={walletsError}>
          <Button type="primary" onClick={loadWallets}>
            Спробувати ще раз
          </Button>
        </Problem>
      )
    }

    if (!wallets.length) {
      return (
        <Problem
          title="Немає доступних рахунків"
          text="На цьому номері немає рахунків, з яких можна заправити авто."
        />
      )
    }

    return (
      <RefuelBody
        pay={pay}
        accountPicker={false}
        walletPicker={{
          wallets,
          wallet,
          onWallet: setWallet,
          balance: pay.balanceError ? undefined : pay.walletBalance,
          loading: pay.loadingBalance,
          error: pay.balanceError,
        }}
      />
    )
  }

  return (
    <div className="wogpay">
      <header className="wogpay__header">
        <div className="wogpay__brand">
          <div className="header-logo">
            <Logo />
          </div>
          <span>Pay</span>
        </div>
        <Button
          className="wogpay__help"
          type="text"
          shape="circle"
          icon={<QuestionCircleOutlined />}
          title="Як це працює"
          aria-label="Як це працює"
          onClick={() => setGuide(true)}
        />
      </header>

      <main className="wogpay__main">
        <section className="wogpay__intro">
          <h1>Заправка через WOG Pay</h1>
          <p>
            Оберіть рахунок, АЗК і кількість літрів — і колонка розпочне
            заправку.
          </p>
        </section>

        <div className="refuel-page">
          {flow && (
            <div className="refuel-page__header">
              <RefuelHeader pay={pay} firstStepCaption={FIRST_STEP} />
            </div>
          )}
          <div className="refuel-page__body">{body()}</div>
          {flow && (
            <div className="refuel-page__footer">
              <RefuelFooter pay={pay} closable={false} />
            </div>
          )}
        </div>
      </main>

      <footer className="wogpay__footer">
        {`COPYRIGHT © ${new Date().getFullYear()} WOG. ALL RIGHTS RESERVED`}
      </footer>

      <Guide visible={guide} onClose={() => setGuide(false)} />
    </div>
  )
}

export default WogPay
